/**
 * This is adjust from https://github.com/okonet/react-container-dimensions
 * For detect print media
 * And we only need width dimension
 */

import type { ReactElement } from 'react';
import React, { useLayoutEffect, useRef, useState, useCallback } from 'react';
import MediaQuery from 'react-responsive';
import elementResizeDetectorMaker from 'element-resize-detector';
import styled from 'styled-components';

const DEFAULT_WIDTH = 704;
interface PrintContainerDimensionsProps {
  children: ({ width, print }: { width: number; print: boolean }) => ReactElement<unknown>;
}

const elementResizeDetector: elementResizeDetectorMaker.Erd = elementResizeDetectorMaker({
  strategy: 'scroll',
  callOnAdd: false,
});

// TODO(VER-87): PERFORMANCE: verify how this is used, its performance, and check for optimizations. It seems like sometimes it can cause excessive rerenders.
/**
 * Wraps the content with a media query for print state, and also sets an explicit width on the child based on the parents width.
 *
 * Be careful not to cause render loops where the child resizes, causing the parent to resize, causing the child to resize.
 *
 * There are often better ways to do this, so consider if you truly need this component.
 * * Alternatives to using this component when you need print media state is to use CSS media queries, use isPrintingState from venn-state,
 * or use StudioInPrintModeContext from venn-components.
 * * Alternatives to using this component for pstrny measurement are to use responsive layout techniques (Flexbox, Grid, % width such as
 * calc(100% - ${padding}px)), relative/absolute positioning, etc).
 */
const PrintContainerDimensions = React.memo(function PrintContainerDimensions({
  children,
}: PrintContainerDimensionsProps) {
  const parentElement = useRef<HTMLElement | null>(null);
  const mounted = useRef(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number>(DEFAULT_WIDTH);
  const [isPrint, setIsPrint] = useState<boolean>(false);

  const getWidth = useCallback((): number => {
    if (!parentElement.current) {
      return DEFAULT_WIDTH;
    }
    return parentElement.current.getBoundingClientRect().width;
  }, []);

  const renderContent = useCallback(
    (print: boolean): React.ReactNode => {
      setIsPrint(print);
      if (print) {
        return children({ width: getWidth(), print });
      }
      const renderChildren = children({ width, print });
      if (renderChildren) {
        return renderChildren;
      }
      return <div />;
    },
    [children, getWidth, width],
  );

  const onResize = useCallback((): void => {
    if (mounted.current) {
      setWidth(getWidth());
    }
  }, [getWidth]);

  // TODO: switch to using resize observer rather than the element-resize-detector library
  useLayoutEffect(() => {
    mounted.current = true;
    const node = ref.current;
    if (node) {
      parentElement.current = node.parentElement;
    }

    parentElement.current && elementResizeDetector.listenTo(parentElement.current, onResize);
    onResize();
    return () => {
      mounted.current = false;
      parentElement.current && elementResizeDetector.removeListener(parentElement.current, onResize);
    };
  }, [getWidth, isPrint, onResize]);

  return <MediaQuery print>{(print: boolean) => <Container ref={ref}>{renderContent(print)}</Container>}</MediaQuery>;
});

const Container = styled.div`
  width: 100%;
`;

export default PrintContainerDimensions;
